import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const SELECT = "SELECT";
const genderOptions = [[SELECT, SELECT], ["MALE", "MALE"], ["FEMALE", "FEMALE"]];

const newApplicantFields = [
  { name: "aocode1", label: "AO Code – Area Code", maxLength: 3 },
  { name: "aocode2", label: "AO Code – AO Type", maxLength: 2 },
  { name: "aocode3", label: "AO Code – Range Code", maxLength: 3 },
  { name: "aocode4", label: "AO Code – AO Number", maxLength: 2 },
];

const updateApplicantFields = [
  { name: "pan", label: "PAN Number", maxLength: 10, pan: true, uppercase: true, required: true },
];

const applicantFields = [
  { name: "alname", label: "Applicant Last Name", maxLength: 25, alphaOnly: true, required: true },
  { name: "afname", label: "Applicant First Name", maxLength: 25, alphaOnly: true, required: true },
  { name: "amname", label: "Applicant Middle Name", maxLength: 25, alphaOnly: true },
  { name: "gender", label: "Gender", options: genderOptions, required: true },
  { name: "dob", label: "Date of Birth", type: "date", required: true },
  { name: "flname", label: "Father's Last Name", maxLength: 25, alphaOnly: true, required: true },
  { name: "ffname", label: "Father's First Name", maxLength: 25, alphaOnly: true, required: true },
  { name: "fmname", label: "Father's Middle Name", maxLength: 25, alphaOnly: true },
];

const addressFields = [
  { name: "aadd1", label: "Address Line 1", maxLength: 25, required: true },
  { name: "aadd2", label: "Address Line 2", maxLength: 25, required: true },
  { name: "aadd3", label: "Address Line 3", maxLength: 25, required: true },
  { name: "city", label: "City", maxLength: 12, required: true },
  { name: "dist", label: "District", maxLength: 21, required: true },
  { name: "state", label: "State", maxLength: 14, required: true },
  { name: "pin", label: "PIN", maxLength: 6, numeric: true, pin: true, required: true },
  { name: "mobile", label: "Mobile", maxLength: 10, numeric: true, mobile: true, required: true },
  { name: "email", label: "Email ID", maxLength: 30, email: true },
  { name: "aadhaar", label: "Aadhaar Number", maxLength: 12, numeric: true, aadhaar: true, required: true },
  { name: "aadhaarname", label: "Name as per Aadhaar", maxLength: 50, alphaOnly: true, required: true },
];

const raFields = [
  { name: "fnameCheck", label: "Father is Representative Assessee", checkbox: true },
  { name: "addressCheck", label: "RA address is same as applicant address", checkbox: true },
  { name: "ralname", label: "RA Last Name", maxLength: 25, alphaOnly: true, required: true },
  { name: "rafname", label: "RA First Name", maxLength: 25, alphaOnly: true, required: true },
  { name: "ramname", label: "RA Middle Name", maxLength: 25, alphaOnly: true },
  { name: "raaadhaar", label: "RA Aadhaar Number", maxLength: 12, numeric: true, aadhaar: true, required: true },
  { name: "raadd1", label: "RA Address Line 1", maxLength: 15, required: true },
  { name: "raadd2", label: "RA Address Line 2", maxLength: 17, required: true },
  { name: "raadd3", label: "RA Address Line 3", maxLength: 12, required: true },
  { name: "racity", label: "RA City", maxLength: 12, required: true },
  { name: "radist", label: "RA District", maxLength: 21, required: true },
  { name: "rastate", label: "RA State", maxLength: 14, required: true },
  { name: "rapin", label: "RA PIN", maxLength: 6, numeric: true, pin: true, required: true },
];

function ageFromDob(value) {
  if (!value) return null;
  const dob = new Date(`${value}T00:00:00`);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  if (now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate())) age -= 1;
  return age;
}

function displayDate(value) {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}/${month}/${year}` : value;
}

export default function PANNew() {
  const isUpdate = false;
  const endpoint = isUpdate ? "/FillMyForm/PAN_FORMS/PrintUpdate.php" : "/FillMyForm/PAN_FORMS/PrintNew.php";
  const formCode = isUpdate ? "PAN_UPDATE" : "PAN_NEW";
  const firstFields = isUpdate ? updateApplicantFields : newApplicantFields;
  const personalFields = useMemo(() => [...firstFields, ...applicantFields], [isUpdate]);
  const allFields = useMemo(() => [...personalFields, ...addressFields, ...(!isUpdate ? raFields : [])], [personalFields, isUpdate]);
  const initialData = useMemo(() => Object.fromEntries(allFields.map((field) => [field.name, field.checkbox ? false : field.options ? field.options[0][0] : ""])), [allFields]);
  const storageKey = `smart-solutions-${formCode.toLowerCase()}`;
  const [formData, setFormData] = useState(() => {
    try {
      return { ...initialData, ...JSON.parse(sessionStorage.getItem(storageKey) || "{}") };
    } catch {
      return initialData;
    }
  });
  const [currentTab, setCurrentTab] = useState(0);
  const [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [errors, setErrors] = useState({});
  const [pdfUrl, setPdfUrl] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const nextButtonRef = useRef(null);

  const currentAge = ageFromDob(formData.dob);
  const minorApplicant = !isUpdate && currentAge !== null && currentAge < 18;

  const tabs = useMemo(() => [
    { title: "Applicant", heading: "👤 Applicant Personal Details", fields: personalFields },
    { title: "Address", heading: "🏠 Applicant Address Details", fields: addressFields },
    ...(minorApplicant ? [{ title: "RA Details", heading: "👥 Representative Assessee Details", fields: raFields }] : []),
    { title: "Preview", heading: "✅ Preview", fields: [] },
  ], [minorApplicant, personalFields]);

  const visibleFields = tabs[currentTab]?.fields || [];
  const firstName = visibleFields[0]?.name;
  const lastName = visibleFields.at(-1)?.name;

  useEffect(() => {
    sessionStorage.setItem(storageKey, JSON.stringify(formData));
  }, [formData, storageKey]);

  useEffect(() => {
    if (currentTab >= tabs.length) setCurrentTab(tabs.length - 1);
  }, [currentTab, tabs.length]);

  useEffect(() => {
    if (!firstName) return;
    const frame = requestAnimationFrame(() => document.getElementById(firstName)?.focus());
    return () => cancelAnimationFrame(frame);
  }, [currentTab, firstName]);

  useEffect(() => () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [pdfUrl]);


  useEffect(() => {
    const controller = new AbortController();

    const loadAutofill = async () => {
      try {
        const response = await fetch(
          `/backend/api/autofill.php?form=${encodeURIComponent(formCode)}`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Unable to load autofill details.");
        }

        if (!result.found || !result.data) {
          return;
        }

        setFormData((previous) => ({
          ...previous,

          city: previous.city || result.data.city || "",
          dist: previous.dist || result.data.dist || "",
          state: previous.state || result.data.state || "",
          pin: previous.pin || result.data.pin || "",
        }));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Autofill error:", error);
        }
      }
    };

    loadAutofill();

    return () => {
      controller.abort();
    };
  }, [formCode]);

  const updateValue = (field, rawValue) => {
    let value = field.numeric ? String(rawValue).replace(/\D/g, "") : rawValue;

    if (field.alphaOnly && typeof value === "string") {
      value = value.replace(/[^A-Za-z ]/g, "");
    }

    if (field.uppercase || field.pan) {
      value = String(value).toUpperCase();
    }

    setFormData((previous) => {
      const next = { ...previous, [field.name]: value };

      if (field.name === "fnameCheck") {
        next.ralname = value ? previous.flname : "";
        next.rafname = value ? previous.ffname : "";
        next.ramname = value ? previous.fmname : "";
      }

      if (field.name === "addressCheck") {
        next.raadd1 = value ? previous.aadd1 : "";
        next.raadd2 = value ? previous.aadd2 : "";
        next.raadd3 = value ? previous.aadd3 : "";
        next.racity = value ? previous.city : "";
        next.radist = value ? previous.dist : "";
        next.rastate = value ? previous.state : "";
        next.rapin = value ? previous.pin : "";
      }

      return next;
    });

    setErrors((previous) => ({ ...previous, [field.name]: "" }));
    setPdfError("");
  };

  const errorFor = (field) => {
    const value = String(formData[field.name] ?? "").trim();
    if (field.required && (!value || value === SELECT)) return `${field.label} is required.`;
    if (!value || value === SELECT) return "";
    if (field.alphaOnly && !/^[A-Za-z ]+$/.test(value)) {
      return `${field.label} must contain alphabet letters only.`;
    }
    if (field.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(value)) {
      return "Enter a valid PAN number (e.g. ABCDE1234F).";
    }
    if (field.mobile && !/^\d{10}$/.test(value)) {
      return "Enter a valid 10-digit mobile number.";
    }
    if (field.aadhaar && !/^\d{12}$/.test(value)) {
      return "Aadhaar number must contain exactly 12 digits.";
    }
    if (field.pin && !/^\d{6}$/.test(value)) {
      return "PIN must contain exactly 6 digits.";
    }
    if (field.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "Enter a valid email address.";
    }
    return "";
  };

  const validate = (fields) => {
    const nextErrors = {};
    fields.forEach((field) => {
      const message = errorFor(field);
      if (message) nextErrors[field.name] = message;
    });
    setErrors(nextErrors);
    return nextErrors;
  };

  const goNext = () => {
    if (Object.keys(validate(visibleFields)).length) return;
    const nextTab = Math.min(currentTab + 1, tabs.length - 1);
    setCurrentTab(nextTab);
    setMaxAllowedTab((value) => Math.max(value, nextTab));
    scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    const fieldsToValidate = tabs.flatMap((tab) => tab.fields);
    const validationErrors = validate(fieldsToValidate);

    if (Object.keys(validationErrors).length) {
      const firstError = Object.keys(validationErrors)[0];
      const errorTab = tabs.findIndex((tab) => tab.fields.some((field) => field.name === firstError));
      setCurrentTab(errorTab >= 0 ? errorTab : 0);
      setPdfError(Object.values(validationErrors)[0]);
      requestAnimationFrame(() => document.getElementById(firstError)?.focus());
      return;
    }

    setPdfLoading(true);
    setPdfError("");

    try {
      const body = new FormData();
      const submittedFieldNames = new Set(fieldsToValidate.map((field) => field.name));
      Object.entries(formData).forEach(([key, value]) => {
        if (!submittedFieldNames.has(key)) return;
        if (typeof value === "boolean") {
          if (value) body.append(key, "on");
        } else {
          body.append(key, value === SELECT ? "" : String(value ?? ""));
        }
      });

      const response = await fetch(endpoint, {
        method: "POST",
        body,
        credentials: "include",
        cache: "no-store",
        headers: { Accept: "application/pdf" },
      });
      const type = response.headers.get("content-type") || "";

      if (!response.ok || !type.toLowerCase().includes("application/pdf")) {
        const text = await response.text();
        throw new Error(text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() || `PDF generation failed. HTTP status: ${response.status}`);
      }

      const url = URL.createObjectURL(await response.blob());
      setPdfUrl((oldUrl) => {
        if (oldUrl) URL.revokeObjectURL(oldUrl);
        return url;
      });
      setShowPdf(true);
    } catch (error) {
      setPdfError(error.message || "Unable to generate PDF preview.");
    } finally {
      setPdfLoading(false);
    }
  };

  const renderField = (field) => {
    const error = errors[field.name];

    if (field.checkbox) {
      return (
        <label key={field.name} className="flex items-center gap-3 rounded-lg border p-3">
          <input id={field.name} name={field.name} type="checkbox" checked={!!formData[field.name]} onChange={(event) => updateValue(field, event.target.checked)} className="h-5 w-5" />
          <span>{field.label}</span>
        </label>
      );
    }

    return (
      <div key={field.name} className="space-y-2">
        <Label htmlFor={field.name}>{field.label}{field.required && <span className="text-red-500"> *</span>}</Label>
        {field.options ? (
          <Select value={formData[field.name]} onValueChange={(value) => updateValue(field, value)}>
            <SelectTrigger id={field.name} className={error ? "border-red-500" : ""} onKeyDown={(event) => {
              if (field.name === lastName && event.key === "Tab" && !event.shiftKey) {
                event.preventDefault();
                nextButtonRef.current?.focus();
              }
            }}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>{field.options.map(([value, label]) => <SelectItem key={`${field.name}-${value}`} value={value}>{label}</SelectItem>)}</SelectContent>
          </Select>
        ) : (
          <Input id={field.name} name={field.name} type={field.type || "text"} value={formData[field.name]} maxLength={field.maxLength} inputMode={field.numeric ? "numeric" : undefined} onChange={(event) => updateValue(field, event.target.value)} onKeyDown={(event) => {
            if (field.name === lastName && event.key === "Tab" && !event.shiftKey) {
              event.preventDefault();
              nextButtonRef.current?.focus();
            }
          }} className={error ? "border-red-500" : ""} placeholder={field.label} autoComplete="on" />
        )}
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    );
  };

  if (showPdf && pdfUrl) {
    return <PdfViewerContainer pdfUrl={pdfUrl} title={`PAN ${isUpdate ? "Update" : "New"} Form`} backRoute="/Fill_My_Form" onBack={() => setShowPdf(false)} />;
  }

  const previewGroups = tabs.filter((tab) => tab.fields.length).map((tab) => ({
    title: tab.title,
    rows: tab.fields.filter((field) => !field.checkbox).map((field) => [
      field.label,
      field.name === "dob" ? displayDate(formData[field.name]) : field.options?.find(([value]) => value === formData[field.name])?.[1] || formData[field.name],
    ]),
  }));

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-7xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-3xl">🪪 PAN {isUpdate ? "Update" : "New Application"} Form</CardTitle>
            <p className="text-muted-foreground">Fill all applicable details carefully.</p>
          </CardHeader>
        </Card>

        <div className={`mb-8 grid grid-cols-2 gap-3 ${tabs.length >= 4 ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
          {tabs.map((tab, index) => (
            <Button key={tab.title} type="button" variant={currentTab === index ? "default" : "outline"} disabled={index > maxAllowedTab || pdfLoading} onClick={() => setCurrentTab(index)}>
              {index + 1}. {tab.title}
            </Button>
          ))}
        </div>

        {currentTab < tabs.length - 1 ? (
          <Card>
            <CardHeader><CardTitle>{tabs[currentTab].heading}</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">{visibleFields.map(renderField)}</div>
              {!isUpdate && currentTab === 0 && formData.dob && currentAge !== null && (
                <p className="mt-6 text-sm text-muted-foreground">{minorApplicant ? "Applicant is a minor; Representative Assessee details will be required." : "Applicant is an adult; Representative Assessee details are not required."}</p>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader><CardTitle>✅ Preview</CardTitle></CardHeader>
            <CardContent className="space-y-8">{previewGroups.map((group) => <Preview key={group.title} {...group} />)}</CardContent>
          </Card>
        )}

        {pdfLoading && <div className="mt-6 text-center">Generating PDF, please wait...</div>}
        {pdfError && <div className="mt-6 rounded-lg border border-red-500 p-4 text-red-500">{pdfError}</div>}

        <div className="mt-8 flex justify-between">
          <Button type="button" variant="outline" disabled={!currentTab || pdfLoading} onClick={() => setCurrentTab((value) => Math.max(0, value - 1))}>← Previous</Button>
          {currentTab === tabs.length - 1 ? (
            <Button type="button" disabled={pdfLoading} onClick={submit}>{pdfLoading ? "Generating PDF..." : "Submit"}</Button>
          ) : (
            <Button ref={nextButtonRef} type="button" disabled={pdfLoading} onClick={goNext}>{currentTab === tabs.length - 2 ? "Preview" : "Next →"}</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Preview({ title, rows }) {
  return (
    <div className="rounded-2xl border p-6">
      <h3 className="mb-6 text-center text-xl font-semibold">{title}</h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label} className="grid grid-cols-2 gap-3 border-b pb-2">
            <div className="font-medium text-muted-foreground">{label}</div>
            <div>{value && value !== SELECT ? value : "-"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
